import React, { useEffect, useMemo } from "react";
import { Bar, Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  FormControl,
  MenuItem,
  Select,
  Container,
} from "@mui/material";
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from "react-redux";
import { fetchChartData } from 'store';
import {
  createChart,
  setChartType,
  setData,
  setSelectedX,
  setSelectedY
} from 'store/slices/chartSlice';
import classes from "./Chart.module.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const Chart = ({ chartId = 'default', columnData , isCardHalf = false}) => {
  const dispatch = useDispatch();

  const {
    defaultXaxis,
    defaultYaxis
  } = useSelector((state) => state.segmenter);

  useEffect(() => {
    dispatch(createChart({ chartId }));
  }, [chartId, dispatch]);

  const chartState = useSelector((state) => state.chart.charts?.[chartId] || {});
  const {
    selectedX = defaultXaxis,
    selectedY = defaultYaxis,
    chartType = 'bar',
    aggregatedData = null,
    data = null,
    chartLoading = false,
  } = chartState;

  useEffect(() => {
    if (!selectedX && !selectedY) return;
    dispatch(fetchChartData({ chartId, x_axis: selectedX, y_axis: selectedY }));
  }, [selectedX, selectedY, dispatch, chartId, isCardHalf]);

  useEffect(() => {
    dispatch(setData({ chartId, value: aggregatedData }));
  }, [chartType, aggregatedData, dispatch, chartId]);

  const handleXChange = (event) => {
    dispatch(setSelectedX({ chartId, value: event.target.value }));
  };

  const handleYChange = (event) => {
    dispatch(setSelectedY({ chartId, value: event.target.value }));
  };

  useEffect(() => {
    dispatch(setSelectedX({ chartId, value: selectedX }));
    dispatch(setSelectedY({ chartId, value: selectedY }));
  });

  const handleChartTypeChange = (event) => {
    const newType = event.target.value;
    if (newType === 'scatter') {
      dispatch(setSelectedX({ chartId, value: columnData.numerical_columns[0] }));
    } else {
      dispatch(setSelectedX({ chartId, value: columnData.categorical_columns[0] }));
    }
    dispatch(setChartType({ chartId, value: newType }));
  };

  const calculateScaleOptions = (data) => {
    if (!data || !data.datasets || data.datasets.length === 0) return {};
    const yValues = data.datasets[0].data || [];
    const minValue = Math.min(...yValues);
    const maxValue = Math.max(...yValues);
    const range = maxValue - minValue || 1;
    return {
      min: minValue - range * 0.5,
      max: maxValue + range * 0.5,
      stepSize: range * 0.2,
    };
  };

  const options = useMemo(() => {
    const scaleOptions = calculateScaleOptions(data);
    return {
      responsive: true,
      plugins: {
        legend: false,
        title: { display: false },
      },
      scales: {
        x: { type: "category" },
        y: {
          beginAtZero: false,
          ticks: { ...scaleOptions },
        },
      },
      barThickness: 50,
      maintainAspectRatio: false,
    };
  }, [data]);

  return (
    <Container className={classes.chartWrapper}>
      <div className={classes.chartInnerWrapper}>
        <div className={classes.actionsContainer}>
          <Typography variant="h6" className={classes.actionItem}>
            {'Type : '}
          </Typography>
          <FormControl size="small" className={ isCardHalf  ? classes.formControl : classes.formControlFull}>
            <Select value={chartType} onChange={handleChartTypeChange}>
              <MenuItem value={'bar'}>Bar Chart</MenuItem>
              <MenuItem value={'scatter'}>Scatter Plot</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="h6" className={classes.actionItem}>
            {'X-Axis : '}
          </Typography>
          <FormControl size="small" className={ isCardHalf  ? classes.formControl : classes.formControlFull}>
            <Select value={selectedX} onChange={handleXChange}>
              {chartType === "scatter"
                ? columnData.numerical_columns.map((item, index) => (
                  <MenuItem value={item} key={`${item}${index}`}>{item}</MenuItem>
                ))
                : columnData.categorical_columns.map((item, index) => (
                  <MenuItem value={item} key={`${item}${index}`}>{item}</MenuItem>
                ))}
            </Select>
          </FormControl>

          <Typography variant="h6" className={classes.actionItem}>
            {'Y-Axis : '}
          </Typography>
          <FormControl size="small" className={ isCardHalf  ? classes.formControl : classes.formControlFull}>
            <Select value={selectedY} onChange={handleYChange}>
              {columnData.numerical_columns.map((item, index) => (
                <MenuItem value={item} key={`${item}${index}`}>{item}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {data && !chartLoading ? (
          chartType === "scatter" ? (
            <Scatter data={data} options={options} className={classes.chart} />
          ) : (
            <div style={{ width: '100%', height: '100%' }}>
              <Bar data={data} options={options} className={classes.chart} />
            </div>
          )
        ) : (
          <p>Loading chart data...</p>
        )}
      </div>
    </Container>
  );
};

export default React.memo(Chart);
